import { lazy, Suspense, useEffect, useState } from 'react';
import { HashRouter, Routes, Route } from 'react-router-dom';
import { Spinner } from './components/ui';
import InstallPrompt from './components/shared/InstallPrompt';
import ErrorBoundary from './components/shared/ErrorBoundary';
import ToastProvider from './components/shared/ToastProvider';
import { TopNav, BottomNav } from './components/navigation';
import { useTheme } from './hooks/useTheme';
import { useTranslation } from 'react-i18next';
import { container } from './services/container';
import { SupabaseAuthProvider } from './services/providers/supabase-auth/SupabaseAuthProvider';
import sync from './services/sync';

// Lazy load pages for better initial load performance
const HomePage = lazy(() => import('./components/home/HomePage'));
const ListPage = lazy(() => import('./components/list/ListPage'));
const SettingsPage = lazy(() => import('./components/settings/SettingsPage'));
const LoginPage = lazy(() => import('./components/auth/LoginPage'));
const ResetPasswordPage = lazy(() => import('./components/auth/ResetPasswordPage'));
// Phase A routes
const InviteRedeemRoute = lazy(() => import('./components/invite/InviteRedeemRoute'));
const FriendsPage = lazy(() => import('./components/friends/FriendsPage'));
// Debug routes
const MockLoginRoute = lazy(() => import('./components/debug/MockLoginRoute'));
const UserDebugPanel = lazy(() => import('./components/debug/UserDebugPanel'));

// Loading fallback component
function PageLoader() {
  return (
    <div className="flex items-center justify-center py-12">
      <div className="text-center">
        <Spinner size="lg" className="text-green-500 mx-auto mb-4" />
        <p className="text-gray-600 dark:text-gray-400">Loading...</p>
      </div>
    </div>
  );
}

function AppContent() {
  const { t } = useTranslation();
  useTheme();

  // Auth state is optional — app works without login.
  // SupabaseAuthProvider handles session restore internally.

  // Password recovery: when the user clicks the reset link in their email,
  // supabase-js parses the recovery token from the URL hash and fires a
  // PASSWORD_RECOVERY event. We show the reset UI as a router-independent
  // override so the recovery-token hash doesn't need a matching route.
  // Lazy initializer doubles as the fallback: the provider (subscribed at
  // module load) may have already latched PASSWORD_RECOVERY before this
  // component mounted. The live listener below catches the normal case.
  const [showPasswordReset, setShowPasswordReset] = useState(() => {
    const auth = container.auth;
    return auth instanceof SupabaseAuthProvider ? auth.consumeRecoveryRequest() : false;
  });

  useEffect(() => {
    const auth = container.auth;
    if (!(auth instanceof SupabaseAuthProvider)) return;
    const { data: { subscription } } = auth.getClient().auth.onAuthStateChange((event) => {
      if (event === 'PASSWORD_RECOVERY') {
        setShowPasswordReset(true);
      }
    });
    return () => subscription.unsubscribe();
  }, []);

  // Initialize sync module on app start
  useEffect(() => {
    sync.init();
  }, []);

  return (
    <div className="min-h-screen bg-green-50 dark:bg-gray-900 flex flex-col">
      <a href="#main-content" className="sr-only focus:not-sr-only focus:absolute focus:top-2 focus:left-2 focus:z-50 focus:px-4 focus:py-2 focus:bg-green-500 focus:text-white focus:rounded-lg focus:outline-none">
        {t('skipToContent')}
      </a>
      <TopNav />
      <main className="flex-1 pb-20 md:pb-0" id="main-content">
        <div className="max-w-lg md:max-w-2xl lg:max-w-5xl mx-auto px-4">
          <Suspense fallback={<PageLoader />}>
            {showPasswordReset ? (
              <ResetPasswordPage onDone={() => setShowPasswordReset(false)} />
            ) : (
              <Routes>
                <Route path="/" element={<HomePage />} />
                <Route path="/settings" element={<SettingsPage />} />
                <Route path="/login" element={<LoginPage />} />
                <Route path="/invite/:token" element={<InviteRedeemRoute />} />
                <Route path="/friends" element={<FriendsPage />} />
                <Route path="/debug/mock-login" element={<MockLoginRoute />} />
                <Route path="/:slug" element={<ListPage />} />
              </Routes>
            )}
          </Suspense>
        </div>
      </main>
      <BottomNav />
      <ToastProvider />
      <InstallPrompt delayMs={5000} />
      <Suspense fallback={null}>
        <UserDebugPanel />
      </Suspense>
    </div>
  );
}

function App() {
  return (
    <ErrorBoundary>
      <HashRouter>
        <AppContent />
      </HashRouter>
    </ErrorBoundary>
  );
}

export default App;
